import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { businessUnitHooks, sectorHooks } from './hooks';
export function BusinessUnitsTab() {
    const tenantId = useTenantId();
    const sectorsQuery = sectorHooks.useList({
        companyId: tenantId
    });
    const { data, isLoading } = businessUnitHooks.useList({
        companyId: tenantId
    });
    const upsert = businessUnitHooks.useUpsert();
    const remove = businessUnitHooks.useDelete();
    const items = data?.items ?? [];
    const [formOpen, setFormOpen] = useState(false);
    // Store the selected id and DERIVE the record from `items` (no re-sync effect).
    const [detailId, setDetailId] = useState(null);
    const detail = detailId ? items.find((b)=>b.id === detailId) ?? null : null;
    const [detailMode, setDetailMode] = useState('read');
    const [deleting, setDeleting] = useState(null);
    const sectors = sectorsQuery.data?.items ?? [];
    const sectorById = useMemo(()=>{
        const m = new Map();
        for (const s of sectors)m.set(s.id, s);
        return m;
    }, [
        sectors
    ]);
    const sectorOptions = useMemo(()=>sectors.map((s)=>({
                value: s.id,
                label: s.name
            })), [
        sectors
    ]);
    const fields = useMemo(()=>[
            {
                kind: 'select',
                name: 'sectorId',
                label: 'Sector',
                options: sectorOptions
            },
            {
                kind: 'text',
                name: 'name',
                label: 'Business unit name',
                required: true,
                maxLength: 200
            },
            {
                kind: 'text',
                name: 'managerName',
                label: 'Manager name',
                maxLength: 200
            }
        ], [
        sectorOptions
    ]);
    const columns = useMemo(()=>[
            {
                key: 'name',
                header: 'Name'
            },
            {
                key: 'sectorId',
                header: 'Sector',
                render: (row)=>sectorById.get(row.sectorId)?.name ?? '—'
            },
            {
                key: 'managerName',
                header: 'Manager'
            }
        ], [
        sectorById
    ]);
    // Read sections plus, per row, the inline-edit control — reusing the same
    // field kinds/options as the create form.
    const buDetailSections = (b)=>[
            {
                rows: [
                    {
                        label: 'Sector',
                        value: sectorById.get(b.sectorId)?.name ?? '—',
                        editField: {
                            kind: 'select',
                            name: 'sectorId',
                            label: 'Sector',
                            options: sectorOptions
                        }
                    },
                    {
                        label: 'Name',
                        value: b.name || '—',
                        editField: {
                            kind: 'text',
                            name: 'name',
                            label: 'Business unit name',
                            required: true,
                            maxLength: 200
                        }
                    },
                    {
                        label: 'Manager',
                        value: b.managerName || '—',
                        editField: {
                            kind: 'text',
                            name: 'managerName',
                            label: 'Manager name',
                            maxLength: 200
                        }
                    }
                ]
            }
        ];
    const submitBU = async (values, id)=>{
        await upsert.mutateAsync({
            id,
            companyId: tenantId,
            sectorId: values.sectorId,
            name: values.name,
            managerName: values.managerName ?? ''
        });
    };
    const detailEdit = detail ? {
        defaultValues: {
            id: detail.id,
            sectorId: detail.sectorId ?? '',
            name: detail.name ?? '',
            managerName: detail.managerName ?? ''
        },
        onSubmit: (v)=>submitBU(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes'
    } : undefined;
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>setFormOpen(true),
                    disabled: sectors.length === 0,
                    "data-testid": Testids.deliveryOrg.businessUnitCreate,
                    children: "New Business Unit"
                })
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryOrg.businessUnitsTable,
                rowTestid: Testids.deliveryOrg.businessUnitRow,
                rowKey: (b)=>b.id,
                isLoading: isLoading,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryOrg.businessUnitEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryOrg.businessUnitDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Create Business Unit",
                fields: fields,
                defaultValues: {
                    id: '',
                    sectorId: '',
                    name: '',
                    managerName: ''
                },
                onSubmit: async (v)=>{
                    await submitBU(v, '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? detail.name || 'Business Unit' : '',
                sections: detail ? buDetailSections(detail) : [],
                testid: Testids.deliveryOrg.businessUnitDetailDrawer,
                edit: detailEdit,
                canEdit: true,
                initialMode: detailMode
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Business Unit",
                description: deleting ? `Permanently delete "${deleting.name}"? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            })
        ]
    });
}
